import AsyncStorage from '@react-native-async-storage/async-storage';
import { useAppStore } from '../store/useAppStore';

// ─────────────────────────────────────────────────────────────────────────
// RIDE SESSION STORE — Fuente de verdad LOCAL de "en qué viaje estoy".
// ─────────────────────────────────────────────────────────────────────────
// Problema que resuelve:
//   1) VIAJE FANTASMA — Al conectarse el chofer, un ride viejo ACCEPTED con
//      su driverId (de pruebas anteriores) resucitaba como "Viaje en Curso".
//      El filtro por antigüedad no basta porque un ride de HOY sigue dentro
//      de la ventana. La solución robusta: la app solo restaura un ride si su
//      ID coincide con el que YO guardé localmente al entrar genuinamente.
//   2) BACKGROUND / RESUME — Cuando Android mata el proceso en segundo plano,
//      al reabrir la app hace cold-start. Necesitamos saber, de forma
//      determinística y offline, si el usuario estaba en un viaje y en qué
//      estado, para restaurar en vez de mostrar la pantalla de solicitud.
//
// Uber/InDrive hacen exactamente esto: el "current trip id" vive en el device
// y el backend solo se consulta para el ESTADO de ESE trip. Un trip que el
// device no conoce NO se muestra.
// ─────────────────────────────────────────────────────────────────────────

const K_ACTIVE_RIDE_ID = 'GS_ACTIVE_RIDE_ID';
const K_ACTIVE_RIDE_ROLE = 'GS_ACTIVE_RIDE_ROLE';
const K_DRIVER_PENDING_OFFER = 'GS_DRIVER_PENDING_OFFER';
const K_DRIVER_ONLINE = 'GS_DRIVER_ONLINE';
// V36 — uid de la cuenta que entró al viaje. Con él, cerrar sesión ya no
// tiene que borrar el puntero: si vuelve la MISMA cuenta ve su viaje, y si
// entra OTRA el puntero se descarta al leerlo.
const K_ACTIVE_RIDE_OWNER = 'GS_ACTIVE_RIDE_OWNER';

// V35/V55 — "En línea" pedido EN ESTA CARGA de la app. En web la intención
// guardada sobrevive a recargar la pestaña, pero el mapa arranca en
// "Conectarse": solo lo que se encendió desde que cargó la página es real.
let enLineaEnEstaCarga = false;

function uidActual(): string | null {
  try {
    return useAppStore.getState().userFirebaseUid ?? null;
  } catch {
    return null;
  }
}

export type RideSessionRole = 'PASSENGER' | 'DRIVER';

export interface RideSessionSnapshot {
  activeRideId: string | null;
  activeRideRole: RideSessionRole | null;
  pendingOfferRideId: string | null;
  driverOnline: boolean;
}

export const rideSessionStore = {
  /**
   * Marca que el usuario ENTRÓ a un viaje concreto (pasajero al crear el
   * ride + aceptar chofer; chofer cuando el pasajero acepta su oferta).
   * A partir de aquí el resume restaurará ESTE ride y ningún otro.
   */
  async setActiveRide(rideId: string, role: RideSessionRole): Promise<void> {
    try {
      const dueno = uidActual();
      await AsyncStorage.multiSet([
        [K_ACTIVE_RIDE_ID, rideId],
        [K_ACTIVE_RIDE_ROLE, role],
      ]);
      // Sin uid no se puede afirmar de quién es: se quita el de un viaje viejo
      // para no atribuírselo a otra cuenta.
      if (dueno) await AsyncStorage.setItem(K_ACTIVE_RIDE_OWNER, dueno);
      else await AsyncStorage.removeItem(K_ACTIVE_RIDE_OWNER);
    } catch (e) {
      console.warn('[rideSession] setActiveRide falló:', (e as any)?.message);
    }
  },

  /** Limpia el viaje activo — al completar / cancelar / terminar. */
  async clearActiveRide(): Promise<void> {
    try {
      await AsyncStorage.multiRemove([K_ACTIVE_RIDE_ID, K_ACTIVE_RIDE_ROLE, K_ACTIVE_RIDE_OWNER]);
    } catch (e) {
      console.warn('[rideSession] clearActiveRide falló:', (e as any)?.message);
    }
  },

  /**
   * Chofer: guarda el rideId de la oferta que envió y está esperando que el
   * pasajero acepte. El listener global del chofer usa esto para distinguir
   * "mi oferta fue aceptada" de "ride fantasma con mi driverId".
   */
  async setPendingOffer(rideId: string | null): Promise<void> {
    try {
      if (rideId) await AsyncStorage.setItem(K_DRIVER_PENDING_OFFER, rideId);
      else await AsyncStorage.removeItem(K_DRIVER_PENDING_OFFER);
    } catch (e) {
      console.warn('[rideSession] setPendingOffer falló:', (e as any)?.message);
    }
  },

  /** Chofer: intención de estar online (respaldo si el GPS task muere). */
  async setDriverOnline(online: boolean): Promise<void> {
    enLineaEnEstaCarga = online;
    try {
      if (online) await AsyncStorage.setItem(K_DRIVER_ONLINE, '1');
      else await AsyncStorage.removeItem(K_DRIVER_ONLINE);
    } catch (e) {
      console.warn('[rideSession] setDriverOnline falló:', (e as any)?.message);
    }
  },

  /**
   * V35/V55 — true solo si en ESTA carga de la app se pasó a "En línea" y no se
   * volvió a apagar. No lee el disco: la intención guardada de una carga
   * anterior no cuenta.
   */
  enLineaEnEstaCarga(): boolean {
    return enLineaEnEstaCarga;
  },

  /** Lee TODO el snapshot de sesión de una sola vez (para el bootstrap). */
  async read(): Promise<RideSessionSnapshot> {
    try {
      const pairs = await AsyncStorage.multiGet([
        K_ACTIVE_RIDE_ID,
        K_ACTIVE_RIDE_ROLE,
        K_DRIVER_PENDING_OFFER,
        K_DRIVER_ONLINE,
        K_ACTIVE_RIDE_OWNER,
      ]);
      const map = Object.fromEntries(pairs);
      let rideId = map[K_ACTIVE_RIDE_ID] || null;
      let role = map[K_ACTIVE_RIDE_ROLE];
      // V36 — el viaje de OTRA cuenta no se entrega. Solo se descarta cuando se
      // sabe quién es cada uno; si el store aún no tiene uid, decide la
      // comprobación de participante del mapa (H29).
      const dueno = map[K_ACTIVE_RIDE_OWNER] || null;
      const yo = uidActual();
      if (rideId && dueno && yo && dueno !== yo) {
        rideId = null;
        role = null;
        AsyncStorage.multiRemove([K_ACTIVE_RIDE_ID, K_ACTIVE_RIDE_ROLE, K_ACTIVE_RIDE_OWNER]).catch(() => {});
      }
      return {
        activeRideId: rideId,
        activeRideRole: role === 'PASSENGER' || role === 'DRIVER' ? role : null,
        pendingOfferRideId: map[K_DRIVER_PENDING_OFFER] || null,
        driverOnline: map[K_DRIVER_ONLINE] === '1',
      };
    } catch (e) {
      console.warn('[rideSession] read falló:', (e as any)?.message);
      return { activeRideId: null, activeRideRole: null, pendingOfferRideId: null, driverOnline: false };
    }
  },

  /**
   * V36 — al CERRAR SESIÓN. Se borran la oferta pendiente y la intención de
   * estar en línea, pero se CONSERVA el puntero del viaje si lleva dueño: el
   * viaje sigue vivo en el servidor y, al volver a entrar, esa misma cuenta
   * tiene que verlo (si no, "Solicitar viaje" le responde "ya tienes un viaje
   * en curso" sin forma de abrirlo). Otra cuenta no lo hereda: `read` lo
   * descarta. Un puntero sin dueño (guardado antes de este cambio) no se puede
   * atribuir y se borra, como antes.
   */
  async clearAlCerrarSesion(): Promise<void> {
    enLineaEnEstaCarga = false;
    try {
      const dueno = await AsyncStorage.getItem(K_ACTIVE_RIDE_OWNER);
      const claves = [K_DRIVER_PENDING_OFFER, K_DRIVER_ONLINE];
      if (!dueno) claves.push(K_ACTIVE_RIDE_ID, K_ACTIVE_RIDE_ROLE, K_ACTIVE_RIDE_OWNER);
      await AsyncStorage.multiRemove(claves);
    } catch (e) {
      console.warn('[rideSession] clearAlCerrarSesion falló:', (e as any)?.message);
    }
  },

  /** Borra TODO, puntero del viaje incluido. */
  async clearAll(): Promise<void> {
    enLineaEnEstaCarga = false;
    try {
      await AsyncStorage.multiRemove([
        K_ACTIVE_RIDE_ID,
        K_ACTIVE_RIDE_ROLE,
        K_ACTIVE_RIDE_OWNER,
        K_DRIVER_PENDING_OFFER,
        K_DRIVER_ONLINE,
      ]);
    } catch (e) {
      console.warn('[rideSession] clearAll falló:', (e as any)?.message);
    }
  },
};
